import {
  createScene,
  fixSceneField,
  placeScenes,
  updateLesson,
  updateQuestion,
  updateScene,
} from '@platform/client';
import { defined } from '@platform/schema';
import { useCallback, useReducer, useRef } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';

import { CHANGE_THE_SCENES, SAVE, undoStep } from './edits';
import { FIX_WITH_AI, fixStep, REVERT } from './fixes';
import {
  NOTHING_DONE,
  redoable,
  reduceHistory,
  together,
  undoable,
} from './history';
import { arcAfter, sceneSeed } from './order';

import type { FieldWrite, LessonWrite } from './edits';
import type { FixTarget } from './fixes';
import type { Step } from './history';
import type { FixSceneFieldResponse } from '@platform/client';
import type { ArcChange } from '@platform/schema/chat';
import type { LessonWorkspace } from '@platform/schema/review';

/**
 * Saving a lesson's fields, and the way back out of each one — GEN-43.
 *
 * Kept out of `ReviewWorkspace` because that component already carries eight mutations and
 * a preview bridge. This is also the only layer holding both the payload and the write,
 * which is what `undoStep` needs to derive a previous value the form need not remember.
 *
 * Several lessons since GEN-70, and one history across them: a stack per lesson would mean
 * an operator who applied three edits had three ⌘Z's to find.
 */
export interface Editing {
  /**
   * Save a field, or change the arc. Recording the way back is this layer's job and not
   * the caller's — including deciding, for the arc, that there is none.
   */
  save: (write: LessonWrite) => void;
  /** Several field writes as one undo step — ADMIN-27's Apply all. */
  saveAll: (writes: readonly FieldWrite[]) => void;
  undo: () => void;
  redo: () => void;
  /** The steps the buttons name, absent when there is nothing either way. */
  back: Step<readonly FieldWrite[]> | undefined;
  forward: Step<readonly FieldWrite[]> | undefined;
  pending: boolean;
  /**
   * Fix with AI: the API rewrites and saves the field, and the fix becomes one undo
   * step like a hand edit — the card's answer. Rejects when nothing was saved.
   */
  fix: (target: FixTarget) => Promise<FixSceneFieldResponse>;
  /** Put back the text a fix replaced, as its own undoable step. */
  revert: (target: FixTarget, fix: FixSceneFieldResponse) => Promise<void>;
}

/**
 * Where an id now lives. A hand edit to a scene or question learners are served lands on
 * a copy (`workingScene`), and the response names it; an undo still holding the old id
 * would be refused with a 409, because the lesson no longer places that row.
 */
type Moved = Map<string, string>;

const latest = (moved: Moved, id: string): string => {
  let at = id;
  for (let next = moved.get(at); next !== undefined; next = moved.get(at))
    at = next;
  return at;
};

async function sendOne(write: FieldWrite, moved: Moved): Promise<void> {
  if (write.kind === 'lesson') {
    const { personas, ...body } = write.body;

    await updateLesson({
      path: { id: write.lessonId },
      body: {
        ...body,
        // `defined` is shallow, and a label the reviewer left ungraded carries an
        // explicit `fit: undefined` the DTO refuses under exactOptionalPropertyTypes.
        ...(personas === undefined ? {} : { personas: personas.map(defined) }),
      },
      throwOnError: true,
    });
    return;
  }

  const id = latest(
    moved,
    write.kind === 'scene' ? write.sceneId : write.questionId,
  );
  const { data } =
    write.kind === 'scene'
      ? await updateScene({
          path: { id },
          body: write.body,
          throwOnError: true,
        })
      : await updateQuestion({
          path: { id },
          body: { explanation: write.explanation },
          throwOnError: true,
        });

  if (data.id !== id) moved.set(id, data.id);
}

export function useEditing(
  lessons: readonly LessonWorkspace[],
  /** Told which lesson moved, so a screen holding eight does not re-read all of them. */
  refresh: (lessonId: string) => Promise<void>,
): Editing {
  const moved = useRef<Moved>(new Map());

  const edit = useTrackedMutation(SAVE, {
    // In order, because two writes in one batch may touch the same row.
    mutationFn: async (writes: readonly FieldWrite[]) => {
      for (const write of writes) await sendOne(write, moved.current);
    },
    onSuccess: (_written, writes) =>
      Promise.all(
        [...new Set(writes.map((write) => write.lessonId))].map(refresh),
      ),
  });

  /**
   * The arc — which scenes a lesson has and in what order, GEN-45, moved here by GEN-77.
   *
   * A second mutation rather than a fourth arm of the one above: the two are different
   * acts on the action feed. It lives here rather than in `ReviewWorkspace` so the chat's
   * arc proposals reach it without a second copy — `docs/decisions/planner-proposals.md`.
   *
   * Every shape goes through the same wholesale `placeScenes`, the one write the API
   * offers. A removal is a list without that scene: `dropUnreachable` then deletes the
   * scene only if nothing else places it, which is what retires a `retype`'s old one too.
   */
  const arc = useTrackedMutation(CHANGE_THE_SCENES, {
    mutationFn: async ({
      lessonId,
      change,
    }: {
      lessonId: string;
      change: ArcChange;
    }) => {
      const lesson = lessons.find((one) => one.id === lessonId);
      if (lesson === undefined) return;

      // Adding is the one shape that writes before it decides: a scene has to exist to be
      // placed, so it is created first and a failed create never leaves a gap in the order.
      const created =
        change.op === 'add' || change.op === 'retype'
          ? (
              await createScene({
                body: sceneSeed(change.scene),
                throwOnError: true,
              })
            ).data.id
          : undefined;

      await placeScenes({
        path: { id: lessonId },
        body: {
          scenes: arcAfter(change, lesson.scenes, created).map(
            (scene, index) => ({ sceneId: scene.id, position: index + 1 }),
          ),
        },
        throwOnError: true,
      });
    },
    onSuccess: (_placed, { lessonId }) => refresh(lessonId),
  });

  /**
   * Per lesson and per visit, which is what an autosaving form can honestly offer: it
   * holds the values it saw go past, not a history the API keeps. Restoring a lesson to
   * yesterday is a content versioning feature and out of scope by name.
   */
  const [history, remember] = useReducer(
    reduceHistory<readonly FieldWrite[]>,
    NOTHING_DONE,
  );

  const { mutate } = edit;
  const { mutate: place } = arc;

  const stepFor = useCallback(
    (target: FixTarget, fix: FixSceneFieldResponse) => {
      const scene = lessons
        .find((one) => one.id === target.lessonId)
        ?.scenes.find((one) => one.id === target.sceneId);

      return scene === undefined ? undefined : fixStep(scene, target, fix);
    },
    [lessons],
  );

  const fixer = useTrackedMutation(FIX_WITH_AI, {
    mutationFn: async (target: FixTarget) =>
      (
        await fixSceneField({
          path: { id: target.sceneId },
          body: { lessonId: target.lessonId, field: target.field },
          throwOnError: true,
        })
      ).data,
    onSuccess: (fix, target) => {
      const step = stepFor(target, fix);
      if (step !== undefined) remember({ kind: 'did', step: together([step]) });

      return refresh(target.lessonId);
    },
  });

  const reverter = useTrackedMutation(REVERT, {
    mutationFn: async (step: Step<FieldWrite>) => {
      await sendOne(step.before, moved.current);
    },
    onSuccess: (_sent, step) => {
      remember({
        kind: 'did',
        step: together([
          { label: step.label, before: step.after, after: step.before },
        ]),
      });

      return refresh(step.before.lessonId);
    },
  });

  const { mutateAsync: fix } = fixer;
  const { mutateAsync: putBack } = reverter;

  const revert = useCallback(
    async (target: FixTarget, applied: FixSceneFieldResponse) => {
      const step = stepFor(target, applied);
      if (step !== undefined) await putBack(step);
    },
    [putBack, stepFor],
  );

  const saveAll = useCallback(
    (writes: readonly FieldWrite[]) => {
      const steps = writes.flatMap((write) => undoStep(lessons, write) ?? []);
      // Counted from what the step holds, so the button never names a write it skips.
      if (steps.length > 0) remember({ kind: 'did', step: together(steps) });

      mutate(writes);
    },
    [lessons, mutate],
  );

  const save = useCallback(
    (write: LessonWrite) => {
      if (write.kind === 'arc') {
        place({ lessonId: write.lessonId, change: write.change });
        return;
      }

      saveAll([write]);
    },
    [place, saveAll],
  );

  const back = undoable(history);
  const forward = redoable(history);

  // Neither records a step: an undo that pushed its own inverse would make redo reachable
  // only by undoing twice, and the stack would grow on the way out.
  const undo = useCallback(() => {
    if (back === undefined) return;

    remember({ kind: 'undo' });
    mutate(back.before);
  }, [back, mutate]);

  const redo = useCallback(() => {
    if (forward === undefined) return;

    remember({ kind: 'redo' });
    mutate(forward.after);
  }, [forward, mutate]);

  return {
    save,
    saveAll,
    undo,
    redo,
    back,
    forward,
    pending:
      edit.isPending || arc.isPending || fixer.isPending || reverter.isPending,
    fix,
    revert,
  };
}
